/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Collapse,
  Input,
  message,
  Radio,
  Row,
  Spin,
} from 'antd';
import { CollapseProps } from 'antd/lib/collapse';
import { useIntl } from 'react-intl';
import { queryCache } from 'react-query';

import UploadContext from '~src/contexts/UploadContext';
import MobileViewContext from '~src/contexts/MobileViewContext';
import FileProgressContext, {
  FileProgress,
} from '~src/contexts/FileProgressContext';
import icon from '~components/_common/icon/icon';
import IconButton from '~components/_common/IconButton/IconButton';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { fadeInKeyframes } from '~src/styles/animations';
import { useCurrentFolder } from '~src/hooks/useCurrentFolder';
import { usePreventNavigation } from '~src/hooks/usePreventNavigation';
import { formatBytes } from '~src/utils/formatBytes';
import {
  buildBulkUploadSummary,
  createBulkFoldersInBatches,
  findFolderForFile,
  getRelativeFolderPath,
} from '~src/utils/bulkUploadUtils';
import {
  createCommit,
  createFolder,
  deleteCommit,
  deleteFolder,
} from '~src/services/api';
import { useProjectId } from '~src/hooks/useProjectId';
import { DB_STATE } from '~src/types/enums';
import { appLog } from '~src/utils/log';
import {
  __DEV__,
  BULK_UPLOAD_BATCH_SIZE,
  BULK_UPLOAD_CONCURRENCY,
} from '~src/config';
import { useFolders } from '~src/hooks/api-hooks/useFolders';

export interface BulkUploadPanelProps {
  onCollapse?: CollapseProps['onChange'];
  mobile?: boolean;
}

const PREVIEW_PATHS_LIMIT = 3;

const BulkUploadPanel: React.FC<BulkUploadPanelProps> = ({
  onCollapse,
  mobile,
}) => {
  const style = useStyles(styleFn);
  const intl = useIntl();
  const projectId = useProjectId();
  const currentFolder = useCurrentFolder();
  const currentFolderRef = useRef(currentFolder);
  const { uploadContext, setUploadContext } = useContext(UploadContext);
  const { fileProgressContext, setFileProgressContext } = useContext(
    FileProgressContext
  );
  const { showMobileView } = useContext(MobileViewContext);

  const foldersQuery = useFolders({
    as_admin:
      window.sessionStorage.getItem('appState') === 'superUser'
        ? true
        : undefined,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [commitMessage, setCommitMessage] = useState(
    __DEV__ ? 'This is a test commit' : ''
  );
  const [importantVal, setImportantVal] = useState(false);
  const [bulkUploadMode, setBulkUploadMode] = useState<'default' | 'continue'>(
    'default'
  );

  const hasActiveUploadProgress = fileProgressContext.some(
    (item) => !item.isDone && !item.error
  );

  usePreventNavigation({
    modalFuncProps: {
      content: intl.formatMessage({ id: 'prevent-navigation-upload-message' }),
    },
  });

  const summary = useMemo(() => buildBulkUploadSummary(uploadContext), [
    uploadContext,
  ]);

  const allPreviewPaths = useMemo(() => {
    const uniquePaths = Array.from(
      new Set(
        uploadContext
          .map((file) => getRelativeFolderPath(file))
          .filter((path): path is string => !!path)
      )
    );

    return uniquePaths.sort((a, b) => {
      const depthA = a.split('/').filter(Boolean).length;
      const depthB = b.split('/').filter(Boolean).length;

      if (depthA !== depthB) {
        return depthA - depthB;
      }

      return a.localeCompare(b);
    });
  }, [uploadContext]);

  const previewPaths = useMemo(
    () => allPreviewPaths.slice(0, PREVIEW_PATHS_LIMIT),
    [allPreviewPaths]
  );

  const hiddenPreviewCount = Math.max(
    0,
    allPreviewPaths.length - PREVIEW_PATHS_LIMIT
  );

  useEffect(() => {
    return () => {
      setUploadContext([]);
    };
  }, []);

  const beforeUnloadListener = (e) => {
    e.preventDefault();
    return (e.returnValue = intl.formatMessage({ id: 'you-sure' }));
  };

  useEffect(() => {
    window.addEventListener('beforeunload', beforeUnloadListener, {
      capture: true,
    });

    return () => {
      window.removeEventListener('beforeunload', beforeUnloadListener, {
        capture: true,
      });
    };
  }, []);

  useEffect(() => {
    if (currentFolder?.id !== currentFolderRef.current?.id) {
      setUploadContext([]);
    }
  }, [currentFolder, setUploadContext]);

  useEffect(() => {
    onCollapse && onCollapse('1');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (currentFolder?.id !== currentFolderRef.current?.id) return null;

  const isValid = () => true;

  const doCreateCommit = async () => {
    if (!projectId) throw new Error('Missing project id.');

    const commitData = {
      description: commitMessage,
      is_important: importantVal,
      state: DB_STATE.DRAFT,
    };

    return createCommit({
      projectId: String(projectId),
      commitData,
    }).catch((err) => {
      message.error(intl.formatMessage({ id: 'commit-create-failed' }));
      appLog('BulkUploadPanel -> createCommit -> commit', commitData);
      appLog('BulkUploadPanel -> createCommit -> err', err);
      throw err;
    });
  };

  const handleUploadClick = async () => {
    if (!projectId || !isValid()) return;

    if (hasActiveUploadProgress) {
      message.warning(intl.formatMessage({ id: 'upload-already-in-progress' }));
      return;
    }

    setIsLoading(true);

    let commit;
    let folderList = [];

    try {
      commit = await doCreateCommit();

      folderList = await createBulkFoldersInBatches({
        files: uploadContext,
        currentFolder,
        existingFolders: foldersQuery.data || [],
        batchSize: BULK_UPLOAD_BATCH_SIZE,
        createFolder: async ({ name, parentId }) =>
          createFolder({
            projectId: String(projectId),
            folderData: {
              name,
              parent_id: parentId,
              is_locked: false,
              description: '',
              state: DB_STATE.AVAILABLE,
            },
            commit_id: commit.id,
          }),
        onBatchProgress: ({ processed, total, currentLevel, totalLevels }) => {
          appLog('BulkUploadPanel -> folder batch progress', {
            processed,
            total,
            currentLevel,
            totalLevels,
          });
        },
      });

      const bulkCreatedFolders = folderList.filter((folder) => folder.isNew);

      const progressItems: FileProgress[] = uploadContext.map((file, index) => {
        const folderEntry = findFolderForFile(file, folderList, currentFolder);

        const timestamp = new Date().getTime();
        const fileExtension = file.name.split('.').pop();
        const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');
        const modifiedFileName = `${fileNameWithoutExtension}-${timestamp}.${fileExtension}`;

        const modifiedFile = new File([file], modifiedFileName, {
          type: file.type,
          lastModified: file.lastModified,
        });

        const blacklistNames = ['image'];
        const isBlacklistedName = blacklistNames.includes(
          fileNameWithoutExtension.toLowerCase()
        );

        return {
          id: `${file.name}-${Date.now()}-${index}`,
          commit,
          file: !isBlacklistedName ? file : modifiedFile,
          description: '',
          versionStr: '',
          folder: folderEntry,
          tags: [],
          progress: {
            bytesTotal: 0,
            bytesUploaded: 0,
            percentage: '0',
          },
          entityCreated: false,
          isDone: false,
          projectId,
          shouldStartUpload: index < BULK_UPLOAD_CONCURRENCY,
          bulkCreatedFolders,

          // Bulk upload mode.
          // default = keep current behavior and upload existing files as new versions.
          // continue = skip already uploaded files and upload only missing files.
          bulkUploadMode,
          skipIfAlreadyUploaded: bulkUploadMode === 'continue',
        };
      });

      setFileProgressContext((prevFileProgress) => [
        ...progressItems,
        ...prevFileProgress,
      ]);

      setUploadContext([]);

      appLog('BulkUploadPanel -> handleUploadClick -> ready for upload', {
        commit,
        filesCount: uploadContext.length,
        foldersCount: folderList.length,
        summary,
      });
    } catch (err) {
      appLog('BulkUploadPanel -> handleUploadClick -> err', err);

      if (folderList.length) {
        await Promise.all(
          // old folder delete
          // folderList
          //   .filter((folder) => folder.isNew)
          //   .map((folder) =>
          //     deleteFolder({
          //       projectId: String(projectId),
          //       folder: folder.dbFolder,
          //       commitId: commit?.id,
          //     }).catch(() => null)
          //   )
          folderList
            .filter((folder) => folder.isNew)
            .sort(
              (a, b) =>
                b.path.split('/').filter(Boolean).length -
                a.path.split('/').filter(Boolean).length
            )
            .map((folder) =>
              deleteFolder({
                projectId: String(projectId),
                folder: folder.dbFolder,
                commitId: commit?.id,
              }).catch(() => null)
            )
        ).catch(() => null);

        queryCache.invalidateQueries('folders');
      }

      if (commit) {
        await deleteCommit({
          projectId: String(projectId),
          commit,
        }).catch(() => null);
      }

      message.error(`An error occurred: ${err}`);
    } finally {
      setIsLoading(false);
    }
  };

  const panelHeader = (
    <Row align="middle">
      <IconButton
        iconStyle="far"
        icon="info-circle"
        customStyle={style.icons}
        containerStyle={{ marginLeft: 'auto' }}
        backgroundOff
        tooltip={intl.formatMessage({ id: 'upload-info' })}
      />
      {intl.formatMessage({ id: 'details' })}
    </Row>
  );

  const content = (
    <div css={style.panelViewport}>
      <div css={style.panelScrollArea}>
        <div css={style.panelContainer}>
          <div css={style.panelTitle}>
            {icon('far', '', style.titleBtn)}
            {intl.formatMessage(
              { id: 'upload-panel-header' },
              { noOfFiles: uploadContext.length }
            )}
          </div>

          <div css={style.centerContentContainer}>
            <div css={style.bulkInfoBox}>
              <div css={style.bulkHeaderRow}>
                <div css={style.bulkBadge}>
                  {intl.formatMessage({ id: 'bulk-upload-mode' })}
                </div>

                <Radio.Group
                  css={style.bulkModeRadioGroup}
                  size="small"
                  value={bulkUploadMode}
                  disabled={isLoading || hasActiveUploadProgress}
                  onChange={(event) => setBulkUploadMode(event.target.value)}>
                  <Radio.Button value="default">
                    {intl.formatMessage({ id: 'bulk-upload-mode-default' })}
                  </Radio.Button>

                  <Radio.Button value="continue">
                    {intl.formatMessage({ id: 'bulk-upload-mode-continue' })}
                  </Radio.Button>
                </Radio.Group>
              </div>

              <div css={style.bulkDescription}>
                {intl.formatMessage({ id: 'bulk-upload-mode-description' })}
                <br />
                {intl.formatMessage({
                  id:
                    bulkUploadMode === 'continue'
                      ? 'bulk-upload-mode-continue-description'
                      : 'bulk-upload-mode-description-wider',
                })}
              </div>

              <div css={style.summaryGrid}>
                <div css={style.summaryCard}>
                  <div css={style.summaryLabel}>
                    {intl.formatMessage({ id: 'files' })}
                  </div>
                  <div css={style.summaryValue}>{summary.totalFiles}</div>
                </div>

                <div css={style.summaryCard}>
                  <div css={style.summaryLabel}>
                    {intl.formatMessage({ id: 'size' })}
                  </div>
                  <div css={style.summaryValue}>
                    {formatBytes(summary.totalSize, 1)}
                  </div>
                </div>

                <div css={style.summaryCard}>
                  <div css={style.summaryLabel}>
                    {intl.formatMessage({ id: 'root-folders' })}
                  </div>
                  <div css={style.summaryValue}>{summary.rootFoldersCount}</div>
                </div>

                <div css={style.summaryCard}>
                  <div css={style.summaryLabel}>
                    {intl.formatMessage({ id: 'folders' })}
                  </div>
                  <div css={style.summaryValue}>
                    {summary.uniqueFolderPathsCount}
                  </div>
                </div>

                {/* Nested files preview */}
                {/*<div css={style.summaryCardWide}>*/}
                {/*  <div css={style.summaryLabel}>*/}
                {/*    {intl.formatMessage({ id: 'nested-files' })}*/}
                {/*  </div>*/}
                {/*  <div css={style.summaryValue}>{summary.nestedFilesCount}</div>*/}
                {/*</div>*/}
              </div>

              {previewPaths.length > 0 && (
                <div css={style.previewBlock}>
                  <div css={style.previewHeaderRow}>
                    <div css={style.previewTitle}>
                      {intl.formatMessage({ id: 'folder-structure-preview' })}
                    </div>

                    {hiddenPreviewCount > 0 && (
                      <div css={style.previewMeta}>
                        + {hiddenPreviewCount}{' '}
                        {intl.formatMessage({
                          id: 'more-folders-in-structure',
                        })}
                      </div>
                    )}
                  </div>

                  <div css={style.previewList}>
                    {previewPaths.map((path) => (
                      <div key={path} css={style.previewItem} title={path}>
                        {path}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div css={style.noticeBox}>
                {intl.formatMessage({ id: 'bulk-upload-notice' })}
              </div>
            </div>

            <Input.TextArea
              css={style.commitInput}
              rows={3}
              placeholder={intl.formatMessage({
                id: 'enter-upload-message',
              })}
              value={commitMessage}
              onChange={({ target: { value } }) => setCommitMessage(value)}
            />

            <Checkbox
              css={style.checkbox}
              checked={importantVal}
              onChange={({ target: { checked } }) => setImportantVal(checked)}>
              {intl.formatMessage({ id: 'select-uploads-as-important' })}
            </Checkbox>

            <div css={style.buttonsRow}>
              <Button
                type="default"
                onClick={() => setUploadContext([])}
                css={style.buttons}>
                {intl.formatMessage({ id: 'cancel' })}
              </Button>

              <Button
                type="primary"
                css={style.buttons}
                loading={isLoading}
                disabled={!isValid() || isLoading || hasActiveUploadProgress}
                onClick={handleUploadClick}>
                {intl.formatMessage({ id: 'upload' })}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (mobile) {
    return (
      <div css={showMobileView ? style.containerMobile : style.container}>
        <Collapse
          bordered={false}
          defaultActiveKey={['uploadPanel']}
          css={style.properties}
          expandIconPosition="right"
          onChange={(...args) =>
            onCollapse && setTimeout(() => onCollapse(...args), 200)
          }>
          <Collapse.Panel header={panelHeader} key="uploadPanel">
            {content}
          </Collapse.Panel>
        </Collapse>

        {isLoading && (
          <div css={style.overlay}>
            <Spin size="large" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div css={style.container}>
      <Collapse
        bordered={false}
        defaultActiveKey={['uploadPanel']}
        css={style.properties}
        expandIconPosition="right"
        onChange={(...args) =>
          onCollapse && setTimeout(() => onCollapse(...args), 200)
        }>
        <Collapse.Panel header={panelHeader} key="uploadPanel">
          {content}
        </Collapse.Panel>
      </Collapse>

      {isLoading && (
        <div css={style.overlay}>
          <Spin size="large" />
        </div>
      )}
    </div>
  );
};

export default BulkUploadPanel;

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    position: 'relative',
    overflow: 'hidden',
  },
  containerMobile: {
    position: 'fixed',
    width: '100%',
    bottom: 60,
  },
  properties: {
    color: theme.colors.text.secondary,
    backgroundColor: `${theme.colors.lightGreen} !important`,
  },
  panelViewport: {
    maxHeight: 'calc(100vh - 210px)',
    minHeight: 0,
  },
  panelScrollArea: {
    maxHeight: 'calc(100vh - 210px)',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: 4,
  },
  panelContainer: {
    marginTop: '-35px',
    paddingBottom: 8,
  },
  centerContentContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  panelTitle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '10px',
    color: theme.colors.text.primary,
  },
  bulkInfoBox: {
    width: '100%',
    padding: 14,
    borderRadius: 8,
    background: 'rgba(255,255,255,0.65)',
    border: `1px solid ${theme.colors.border || 'rgba(0,0,0,.08)'}`,
    animation: `${fadeInKeyframes} 0.3s ease-out`,
  },
  bulkBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    background: theme.colors.primary,
    color: '#fff',
  },
  bulkHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 10,
    flexWrap: 'wrap',
  },
  bulkModeRadioGroup: {
    flexShrink: 0,
  },
  bulkDescription: {
    marginBottom: 14,
    color: theme.colors.text.primary,
    lineHeight: 1.45,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 10,
  },
  summaryCard: {
    padding: 12,
    borderRadius: 8,
    background: 'rgba(255,255,255,0.8)',
    border: `1px solid ${theme.colors.border || 'rgba(0,0,0,.06)'}`,
  },
  summaryCardWide: {
    gridColumn: '1 / -1',
    padding: 12,
    borderRadius: 8,
    background: 'rgba(255,255,255,0.8)',
    border: `1px solid ${theme.colors.border || 'rgba(0,0,0,.06)'}`,
  },
  summaryLabel: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginBottom: 6,
  },
  summaryValue: {
    fontSize: 18,
    fontWeight: 700,
    color: theme.colors.text.primary,
    wordBreak: 'break-word',
  },
  previewBlock: {
    marginTop: 14,
  },
  previewHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 8,
    flexWrap: 'wrap',
  },
  previewTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: theme.colors.text.primary,
  },
  previewMeta: {
    fontSize: 12,
    color: theme.colors.text.secondary,
  },
  previewList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  previewItem: {
    fontSize: 12,
    padding: '8px 10px',
    borderRadius: 6,
    background: 'rgba(255,255,255,0.8)',
    border: `1px solid ${theme.colors.border || 'rgba(0,0,0,.06)'}`,
    wordBreak: 'break-word',
  },
  noticeBox: {
    marginTop: 14,
    padding: 10,
    borderRadius: 6,
    fontSize: 12,
    lineHeight: 1.45,
    background: 'rgba(255,255,255,0.8)',
    color: theme.colors.text.secondary,
  },
  commitInput: {
    width: '100%',
    marginTop: 12,
    animation: `${fadeInKeyframes} 0.3s ease-out`,
  },
  checkbox: {
    marginTop: 10,
    alignSelf: 'flex-start',
  },
  buttonsRow: {
    marginTop: 6,
    paddingBottom: 4,
  },
  buttons: {
    margin: '10px 10px 0 10px',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(0,0,0,.15)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  icons: {},
  titleBtn: {},
});
